/**
 * Servicio de Balance
 * Obtiene balances de tokens nativos y ERC-20
 */

import { ethers } from 'ethers';
import config from '@/config';
import { Token } from '@/types';

// Cache de precios (5 minutos)
const priceCache: Record<string, { price: number; change24h?: number; timestamp: number }> = {};
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

/**
 * Servicio de Balance
 */
export const balanceService = {
  /**
   * Obtiene el balance nativo de una dirección
   */
  async fetchNativeBalance(address: string, networkId: string, provider: ethers.Provider): Promise<number> {
    try {
      const network = config.getNetwork(networkId);
      if (!network) {
        console.warn(`Red ${networkId} no encontrada`);
        return 0;
      }

      // Para OMDB, usar el explorer API directamente desde el browser
      // (el RPC tiene CORS bloqueado y la IP del VPS puede estar bloqueada)
      if (networkId === 'omdb' || networkId === 'omdb-local') {
        try {
          const explorerUrl = import.meta.env.VITE_OMDB_API_URL || 'https://omdbscan.com';
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 5000);

          // Barra al final antes del "?" a propósito: sin ella, omdbscan.com
          // responde 301 a "/api/?..." y ese 301 no trae Access-Control-Allow-Origin
          // (solo la respuesta final la trae) — el navegador corta el fetch por CORS
          // antes de llegar a seguir el redirect. Pidiendo "/api/" directo, no hay redirect.
          const response = await fetch(
            `${explorerUrl}/api/?module=account&action=balance&address=${address}&tag=latest`,
            { signal: controller.signal }
          );

          clearTimeout(timeoutId);

          if (!response.ok) throw new Error(`Explorer API error: ${response.status}`);

          const data = await response.json();

          if (data.status === '1' && data.result) {
            const balanceWei = ethers.toBigInt(data.result);
            const balance = parseFloat(ethers.formatUnits(balanceWei, network.nativeCurrency.decimals));
            console.log(`Balance OMDB: ${balance} ${network.nativeCurrency.symbol}`);
            return balance;
          }
        } catch (omdbError) {
          console.error('Error obteniendo balance OMDB via explorer:', omdbError);
        }
        // Si falla el explorer, cae al método RPC de abajo como fallback
      }

      // Método tradicional con timeout
      const balancePromise = provider.getBalance(address);
      const timeoutPromise = new Promise<bigint>((_, reject) =>
        setTimeout(() => reject(new Error('Balance fetch timeout')), 5000)
      );

      const balanceWei = await Promise.race([balancePromise, timeoutPromise]).catch((error) => {
        console.warn(`Timeout obteniendo balance para ${networkId}:`, error);
        return ethers.parseEther('0');
      });

      const balance = parseFloat(ethers.formatUnits(balanceWei, network.nativeCurrency.decimals));

      console.log(`Balance nativo: ${balance} ${network.nativeCurrency.symbol}`);
      return balance;
    } catch (error) {
      console.error('Error al obtener balance nativo:', error);
      return 0;
    }
  },

  /**
   * Obtiene el balance de un token ERC-20
   */
  async fetchTokenBalance(
    address: string,
    tokenAddress: string,
    decimals: number,
    provider: ethers.Provider
  ): Promise<string> {
    try {
      const tokenContract = new ethers.Contract(
        tokenAddress,
        ['function balanceOf(address) view returns (uint256)'],
        provider
      );

      const balanceWei = await tokenContract.balanceOf(address);
      const balance = ethers.formatUnits(balanceWei, decimals);

      return balance;
    } catch (error) {
      console.error(`Error obteniendo balance del token ${tokenAddress}:`, error);
      return '0';
    }
  },

  /**
   * Obtiene balances de múltiples tokens (nativos y ERC-20)
   */
  async fetchMultipleTokenBalances(
    address: string,
    tokens: Token[],
    provider: ethers.Provider,
    networkId?: string
  ): Promise<Token[]> {
    const updatedTokens: Token[] = [];

    for (const token of tokens) {
      try {
        if (token.isNative) {
          // Obtener balance nativo si tenemos el networkId
          if (networkId) {
            const nativeBalance = await this.fetchNativeBalance(address, networkId, provider);
            const balanceStr = nativeBalance.toString();
            updatedTokens.push({
              ...token,
              balance: balanceStr,
              balanceFormatted: nativeBalance.toFixed(6),
            });
          } else {
            // Sin networkId no podemos obtenerlo — mantener el valor existente
            updatedTokens.push(token);
          }
          continue;
        }

        const balance = await this.fetchTokenBalance(address, token.address, token.decimals, provider);

        updatedTokens.push({
          ...token,
          balance,
          balanceFormatted: parseFloat(balance).toFixed(6),
        });
      } catch (error) {
        console.error(`Error obteniendo balance de ${token.symbol}:`, error);
        updatedTokens.push({
          ...token,
          balance: '0',
          balanceFormatted: '0',
        });
      }
    }

    return updatedTokens;
  },

  /**
   * Obtiene el precio de un token
   */
  async fetchTokenPrice(symbol: string): Promise<{ price: number, change24h?: number }> {
    try {
      const normalizedSymbol = symbol.toLowerCase();

      // Verificar cache
      const cached = priceCache[normalizedSymbol];
      if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        console.log(`💰 Precio de ${symbol} desde cache: ${cached.price}`);
        return { price: cached.price, change24h: cached.change24h };
      }

      // OMDB y OMD: una sola llamada a CoinStore devuelve ambos precios
      if (normalizedSymbol === 'omdb' || normalizedSymbol === 'omd') {
        const allPrices = await this.fetchCoinStorePrices();
        const priceData = allPrices[normalizedSymbol as 'omdb' | 'omd'];
        return priceData;
      }

      // Para otros tokens, usar CoinGecko a través del proxy
      const cgData = await this.fetchPriceFromCoinGecko(symbol);
      if (cgData && cgData.price > 0) {
        priceCache[normalizedSymbol] = { price: cgData.price, change24h: cgData.change24h, timestamp: Date.now() };
        return cgData;
      }

      return { price: 0 };
    } catch (error) {
      console.error(`❌ Error obteniendo precio de ${symbol}:`, error);
      return { price: 0 };
    }
  },

  /**
   * Obtiene el precio desde CoinGecko proxy
   */
  async fetchPriceFromCoinGecko(symbol: string): Promise<{ price: number, change24h?: number } | null> {
    try {
      const normalizedSymbol = symbol.toLowerCase();

      // Mapeo de símbolos a IDs de CoinGecko
      const symbolMap: Record<string, string> = {
        eth: 'ethereum',
        bnb: 'binancecoin',
        matic: 'polygon-ecosystem-token',
        pol: 'polygon-ecosystem-token',
        usdt: 'tether',
        usdc: 'usd-coin',
        dai: 'dai',
        weth: 'weth',
        wbnb: 'wbnb',
        busd: 'binance-usd',
        link: 'chainlink',
        uni: 'uniswap',
        aave: 'aave',
        btcb: 'binance-bitcoin',
        cake: 'pancakeswap-token',
        shib: 'shiba-inu',
        arb: 'arbitrum',
        op: 'optimism',
      };

      const coinId = symbolMap[normalizedSymbol] || normalizedSymbol;

      console.log(`🔍 Obteniendo precio de ${symbol} (${coinId}) desde CoinGecko proxy...`);

      const response = await fetch(
        `${config.getApiBaseUrl()}/api/proxy/coingecko/price?ids=${coinId}&vs_currencies=usd`,
        {
          headers: {
            'Accept': 'application/json',
          },
        }
      );

      if (!response.ok) {
        if (response.status === 429) {
          console.warn('⚠️ Rate limit de CoinGecko alcanzado.');
          throw new Error('Rate limit alcanzado');
        }
        throw new Error(`Error HTTP: ${response.status}`);
      }

      const data = await response.json();

      if (data[coinId]?.usd) {
        const price = data[coinId].usd;
        const change24h = data[coinId].usd_24h_change;
        console.log(`✅ Precio de ${symbol} desde proxy: ${price} (24h: ${change24h}%)`);
        return { price, change24h };
      }

      console.warn(`⚠️ No se encontró precio para ${symbol} (${coinId})`);
      return null;
    } catch (error) {
      console.error(`❌ Error obteniendo precio desde proxy CoinGecko:`, error);
      return null;
    }
  },

  /**
   * Obtiene el precio vía proxy local → CoinMarketCap
   * El proxy en /api/proxy/coinmarketcap/price hace la petición server-side
   * evitando el bloqueo CORS del navegador.
   */
  async fetchPriceFromCMC(symbol: string): Promise<number> {
    try {
      console.log(`🔍 Obteniendo precio de ${symbol} vía proxy → CoinMarketCap...`);

      // Ruta relativa en web (mismo origen); absoluta si VITE_API_BASE_URL está definida (app empaquetada)
      const proxyUrl = `${config.getApiBaseUrl()}/api/proxy/coinmarketcap/price`;

      const response = await fetch(proxyUrl, {
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        console.warn(`⚠️ Error HTTP ${response.status} desde proxy CoinMarketCap`);
        return 0;
      }

      const data = await response.json();

      if (data?.success && data?.price) {
        const price = parseFloat(data.price);
        console.log(`✅ Precio de ${symbol} vía proxy CoinMarketCap (${data.source}): $${price.toFixed(6)}`);
        return price;
      }

      console.warn(`⚠️ Respuesta del proxy sin precio:`, JSON.stringify(data).substring(0, 200));
      return 0;

    } catch (error) {
      console.error(`❌ Error obteniendo precio desde proxy CoinMarketCap:`, error);
      return 0;
    }
  },

  /**
   * Obtiene los precios de OMDB y OMD en una sola llamada a CoinStore.
   * Cachea ambos precios al recibir la respuesta.
   */
  async fetchCoinStorePrices(): Promise<{ omdb: { price: number; change24h?: number }; omd: { price: number; change24h?: number } }> {
    const fallback = { omdb: { price: 0 }, omd: { price: 0 } };
    try {
      console.log(`🔍 Obteniendo precios OMDB y OMD vía proxy → CoinStore...`);

      const response = await fetch(`${config.getApiBaseUrl()}/api/proxy/coinstore/prices`, {
        headers: { 'Accept': 'application/json' },
      });

      if (!response.ok) {
        console.warn(`⚠️ Error HTTP ${response.status} desde proxy CoinStore`);
        return fallback;
      }

      const data = await response.json();

      if (!data?.success || !data?.prices) {
        console.warn(`⚠️ Respuesta inesperada de CoinStore:`, JSON.stringify(data).substring(0, 200));
        return fallback;
      }

      const omdb = { price: data.prices.OMDB?.price ?? 0, change24h: data.prices.OMDB?.change24h };
      const omd  = { price: data.prices.OMD?.price ?? 0,  change24h: data.prices.OMD?.change24h };

      priceCache['omdb'] = { ...omdb, timestamp: Date.now() };
      priceCache['omd']  = { ...omd,  timestamp: Date.now() };

      console.log(`✅ CoinStore → OMDB: $${omdb.price} | OMD: $${omd.price}`);
      return { omdb, omd };
    } catch (error) {
      console.error(`❌ Error obteniendo precios desde proxy CoinStore:`, error);
      return fallback;
    }
  },

  /**
   * Obtiene los precios de múltiples tokens optimizando las peticiones (Batched)
   */
  async fetchMultipleTokenPrices(symbols: string[]): Promise<Record<string, { price: number, change24h?: number }>> {
    const results: Record<string, { price: number, change24h?: number }> = {};
    const toFetchCoinGecko: string[] = [];
    let fetchCoinStore = false;

    // Mapeo de símbolos a IDs de CoinGecko
    const symbolMap: Record<string, string> = {
      eth: 'ethereum',
      bnb: 'binancecoin',
      matic: 'polygon-ecosystem-token',
      pol: 'polygon-ecosystem-token',
      usdt: 'tether',
      usdc: 'usd-coin',
      dai: 'dai',
      weth: 'weth',
      wbnb: 'wbnb',
      busd: 'binance-usd',
      link: 'chainlink',
      uni: 'uniswap',
      aave: 'aave',
      btcb: 'binance-bitcoin',
      cake: 'pancakeswap-token',
      shib: 'shiba-inu',
      arb: 'arbitrum',
      op: 'optimism',
    };

    // 1. Revisar caché y separar lo que falta
    for (const symbol of symbols) {
      const normalizedSymbol = symbol.toLowerCase();
      const cached = priceCache[normalizedSymbol];
      
      if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        results[normalizedSymbol] = { price: cached.price, change24h: cached.change24h };
        continue;
      }

      if (normalizedSymbol === 'omdb' || normalizedSymbol === 'omd') {
        fetchCoinStore = true;
      } else {
        const coinId = symbolMap[normalizedSymbol] || normalizedSymbol;
        if (!toFetchCoinGecko.includes(coinId)) {
          toFetchCoinGecko.push(coinId);
        }
      }
    }

    // 2. Obtener precios de OMDB y OMD con una sola llamada a CoinStore
    if (fetchCoinStore) {
      const { omdb, omd } = await this.fetchCoinStorePrices();
      if (!results['omdb']) results['omdb'] = omdb;
      if (!results['omd'])  results['omd']  = omd;
    }

    // 3. Obtener el resto de CoinGecko en una SOLA petición (Batched)
    if (toFetchCoinGecko.length > 0) {
      try {
        const ids = toFetchCoinGecko.join(',');
        console.log(`🔍 Obteniendo precios en bloque (Batch) desde proxy CoinGecko: ${ids}`);
        
        const response = await fetch(`${config.getApiBaseUrl()}/api/proxy/coingecko/price?ids=${ids}&vs_currencies=usd`);
        
        if (response.ok) {
          const data = await response.json();

          for (const symbol of symbols) {
            const normalizedSymbol = symbol.toLowerCase();
            if (normalizedSymbol === 'omdb' || normalizedSymbol === 'omd' || results[normalizedSymbol]) continue;

            const coinId = symbolMap[normalizedSymbol] || normalizedSymbol;
            if (data[coinId]?.usd) {
              const price = data[coinId].usd;
              const change24h = data[coinId].usd_24h_change;

              priceCache[normalizedSymbol] = { price, change24h, timestamp: Date.now() };
              results[normalizedSymbol] = { price, change24h };
            } else {
              results[normalizedSymbol] = { price: 0 };
            }
          }
        }
      } catch (error) {
        console.error(`❌ Error en petición en bloque a CoinGecko:`, error);
      }
    }

    // Asegurar que todos tengan un valor de retorno aunque hayan fallado
    for (const symbol of symbols) {
      const normalizedSymbol = symbol.toLowerCase();
      if (!results[normalizedSymbol]) {
        results[normalizedSymbol] = { price: 0 };
      }
    }

    return results;
  },

  /**
   * Refresca el estado completo de una cuenta (Balances + Precios + Valor Total)
   */
  async refreshFullAccountState(
    address: string,
    networkId: string,
    tokens: Token[],
    provider: ethers.Provider
  ): Promise<{ tokens: Token[]; totalValueUSD: number }> {
    try {
      console.log(`[Balance Service] Refrescando estado completo para ${address} en ${networkId}...`);

      // 1. Obtener balances actualizados (nativos y tokens)
      const updatedTokens = await this.fetchMultipleTokenBalances(address, tokens, provider, networkId);

      // 2. Obtener precios de todos en una sola llamada (Batch)
      const symbolsToFetch = updatedTokens.map(t => t.symbol);
      const pricesMap = await this.fetchMultipleTokenPrices(symbolsToFetch);

      // 3. Unir balances y precios
      const finalTokens: Token[] = [];
      let totalValueUSD = 0;

      for (const token of updatedTokens) {
        const normalizedSymbol = token.symbol.toLowerCase();
        const priceData = pricesMap[normalizedSymbol] || { price: 0 };
        const priceUSD = priceData.price;
        
        const tokenWithPrice = {
          ...token,
          priceUSD: priceUSD > 0 ? priceUSD : token.priceUSD,
          priceChange24h: priceData.change24h !== undefined ? priceData.change24h : token.priceChange24h,
        };

        // Calcular valor en USD para este token
        if (tokenWithPrice.balance && tokenWithPrice.priceUSD) {
          const balance = parseFloat(tokenWithPrice.balance);
          totalValueUSD += balance * tokenWithPrice.priceUSD;
        }

        finalTokens.push(tokenWithPrice);
      }

      console.log(`[Balance Service] Refresco completado. Valor total: $${totalValueUSD.toFixed(2)}`);

      return {
        tokens: finalTokens,
        totalValueUSD,
      };
    } catch (error) {
      console.error('[Balance Service] Error en refresco completo:', error);
      return {
        tokens,
        totalValueUSD: this.calculateTotalValue(tokens),
      };
    }
  },

  /**
   * Calcula el valor total en USD de los tokens
   */
  calculateTotalValue(tokens: Token[]): number {
    let total = 0;

    for (const token of tokens) {
      if (token.balance && token.priceUSD) {
        const balance = parseFloat(token.balance);
        total += balance * token.priceUSD;
      }
    }

    return total;
  },

  /**
   * Refresca balances de TODAS las redes con una sola llamada de precios global.
   * 1. Junta todos los símbolos únicos de todas las redes → 1 batch a CoinGecko/CoinStore
   * 2. Pide los balances por red en paralelo (solo llamadas RPC, sin llamadas de precio extra)
   * 3. Combina balance × precio para calcular el total cross-network
   *
   * CAMBIO: antes esta función solo devolvía el total sumado por red (byNetwork) y
   * descartaba los tokens individuales. Ahora también devuelve `tokens`: la lista
   * completa de tokens de todas las redes, cada uno con su `priceUSD`/`priceChange24h`
   * ya calculado. Esto se usa para poblar `allNetworksTokens` en el store (ver
   * walletStore.ts) y así poder mostrar en el Dashboard/Assets los activos con balance
   * de todas las redes a la vez, no solo de la red seleccionada. No se agregó ninguna
   * llamada nueva a la red, solo se conserva la data que ya se calculaba y se tiraba.
   */
  async refreshAllNetworksBalance(
    address: string,
    networksData: Record<string, { tokens: Token[]; provider: ethers.Provider }>
  ): Promise<{ totalAllNetworks: number; byNetwork: Record<string, number>; tokens: Token[] }> {
    // 1. Un solo batch de precios con todos los símbolos únicos de todas las redes
    const allSymbols = new Set<string>();
    for (const { tokens } of Object.values(networksData)) {
      tokens.forEach(t => allSymbols.add(t.symbol));
    }
    const pricesMap = await this.fetchMultipleTokenPrices([...allSymbols]);

    // 2. Balances por red en paralelo (solo RPC, los precios ya están en pricesMap)
    const byNetwork: Record<string, number> = {};
    // NUEVO: acumula los tokens de todas las redes (con precio incluido) para devolverlos al final
    const allTokens: Token[] = [];

    const results = await Promise.allSettled(
      Object.entries(networksData).map(async ([networkId, { tokens, provider }]) => {
        const updatedTokens = await this.fetchMultipleTokenBalances(address, tokens, provider, networkId);

        let networkTotal = 0;
        // NUEVO: antes solo se sumaba networkTotal y se descartaba updatedTokens.
        // Ahora también armamos tokensWithPrice para no perder el priceUSD/priceChange24h
        // de cada token (misma lógica que refreshFullAccountState, líneas ~466-470).
        const tokensWithPrice: Token[] = [];
        for (const token of updatedTokens) {
          const priceData = pricesMap[token.symbol.toLowerCase()] ?? { price: 0 };
          const balance = parseFloat(token.balance || '0');
          if (balance > 0 && priceData.price > 0) {
            networkTotal += balance * priceData.price;
          }
          tokensWithPrice.push({
            ...token,
            priceUSD: priceData.price > 0 ? priceData.price : token.priceUSD,
            priceChange24h: priceData.change24h !== undefined ? priceData.change24h : token.priceChange24h,
          });
        }

        return { networkId, total: networkTotal, tokens: tokensWithPrice };
      })
    );

    for (const result of results) {
      if (result.status === 'fulfilled') {
        byNetwork[result.value.networkId] = result.value.total;
        // NUEVO: juntamos los tokens de esta red al arreglo total de todas las redes
        allTokens.push(...result.value.tokens);
      }
    }

    const totalAllNetworks = Object.values(byNetwork).reduce((sum, v) => sum + v, 0);
    // NUEVO: se agrega `tokens: allTokens` al resultado (antes solo se devolvía totalAllNetworks y byNetwork)
    return { totalAllNetworks, byNetwork, tokens: allTokens };
  },
};

export default balanceService;
